// Pure budget math. CommonJS so it runs under plain `node` (repo has no test runner).
// Vue components import named exports via Babel interop: `import { sst } from "@/utils/budget"`.

const HQ_LINE_CATEGORIES = [
  "kos_awalan", "kerja_rutin",
  "wps_nod", "wps_emergency", "wps_electrical",
  "wps_pca", "wps_irms", "wps_tot",
];

const EXPENSE_CATEGORIES = ["kos_awalan", "kerja_rutin", "wps_pca", "wps_irms", "wps_tot"];

const DISTRICT_CATEGORIES = ["wps_nod", "wps_emergency", "wps_electrical"];

// Manual (hand-keyed) categories that ARE broken down by district for actual
// spend, even though their allocation ceiling stays HQ-level. Overlaps with
// EXPENSE_CATEGORIES (kerja_rutin is still hand-keyed, still one HQ ceiling) —
// this is an additional classification, not a replacement.
const DISTRICT_EXPENSE_CATEGORIES = ["kerja_rutin"];

function isDistrictExpenseCategory(category) {
  return DISTRICT_EXPENSE_CATEGORIES.indexOf(category) !== -1;
}

const CATEGORY_TO_GROUPTYPE = {
  wps_nod: "kjp",
  wps_emergency: "emergency",
  wps_electrical: "electrical",
};

// Human labels for templates.
const CATEGORY_LABELS = {
  kos_awalan: "Kos awalan kontrak",
  kerja_rutin: "Kerja penyelenggaraan rutin",
  wps_nod: "NOD",
  wps_emergency: "Emergency",
  wps_electrical: "Electrical",
  wps_pca: "PCA",
  wps_irms: "IRMS",
  wps_tot: "ToT",
};

function linesToAmountMap(lines) {
  const map = {};
  (lines || []).forEach((l) => {
    map[l.category] = Number(l.amount) || 0;
  });
  return map;
}

function getAmount(lines, category) {
  return linesToAmountMap(lines)[category] || 0;
}

function wpsFromMap(m) {
  return (
    (m.wps_nod || 0) + (m.wps_emergency || 0) + (m.wps_electrical || 0) +
    (m.wps_pca || 0) + (m.wps_irms || 0) + (m.wps_tot || 0)
  );
}

function wpsTotal(lines) {
  return wpsFromMap(linesToAmountMap(lines));
}

// SST base EXCLUDES kos_awalan: SST = 8% x (kerja_rutin + WPS total).
// (kos_awalan still counts toward the yearly total, just not toward SST.)
function sstBase(lines) {
  const m = linesToAmountMap(lines);
  return (m.kerja_rutin || 0) + wpsFromMap(m);
}

function sst(lines) {
  return Math.round(sstBase(lines) * 0.08 * 100) / 100;
}

// Yearly total = every category (incl. kos_awalan) + SST.
function yearlyTotal(lines) {
  const m = linesToAmountMap(lines);
  return (m.kos_awalan || 0) + (m.kerja_rutin || 0) + wpsFromMap(m) + sst(lines);
}

// Format a raw input string as a thousands-separated amount, allowing a single
// decimal point and up to 2 decimal places (sen). Strips anything else (e, +, -,
// spaces, extra dots). A trailing "." is preserved so the user can keep typing.
function formatAmountInput(raw) {
  let s = String(raw == null ? "" : raw).replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot !== -1) {
    // keep only the first dot
    s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
  }
  let [intPart, decPart] = s.split(".");
  intPart = intPart.replace(/^0+(?=\d)/, ""); // drop leading zeros, keep a lone 0
  const intText = intPart === "" ? "" : Number(intPart).toLocaleString("en-US");
  if (s.indexOf(".") !== -1) {
    decPart = (decPart || "").slice(0, 2);
    return (intText === "" ? "0" : intText) + "." + decPart;
  }
  return intText;
}

// Parse a formatted/raw amount string to a Number, or null when empty/just a dot.
function parseAmountInput(raw) {
  const cleaned = String(raw == null ? "" : raw).replace(/[^\d.]/g, "");
  if (cleaned === "" || cleaned === ".") return null;
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
}

// Resting display: always 2 decimals with thousands separators (e.g. 20000000 ->
// "20,000,000.00"). Used when an amount field is not being edited. "" for null.
function formatAmount2(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (isNaN(n)) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Sum case spend matching a district-breakable category, district, year, and (optional) month.
// `moment` is injected (in the app pass `this.Helper.moment`) so this stays pure/testable.
function caseActual(cases, { category, districtId, year, month, moment }) {
  const group = CATEGORY_TO_GROUPTYPE[category];
  if (!group) return 0;

  return (cases || []).reduce((total, c) => {
    if (!c || c.isRejected) return total;
    if (!c.groupType || !String(c.groupType).toLowerCase().includes(group)) return total;
    if (c.district !== districtId) return total;
    if (!c.nod_bq && !c.nod_claim) return total;

    const m = moment(c.timestamp);
    if (m.year() !== year) return total;
    if (month != null && m.month() + 1 !== month) return total;

    // Mirror Dashboard.vue "used": a falsy/zero claim falls through to the BQ allocation.
    const amount =
      (c.nod_claim && c.nod_claim.amount_used) ||
      (c.nod_bq && c.nod_bq.amount_allocated) ||
      0;
    return total + amount;
  }, 0);
}

module.exports = {
  HQ_LINE_CATEGORIES,
  EXPENSE_CATEGORIES,
  DISTRICT_CATEGORIES,
  DISTRICT_EXPENSE_CATEGORIES,
  isDistrictExpenseCategory,
  CATEGORY_TO_GROUPTYPE,
  CATEGORY_LABELS,
  linesToAmountMap,
  getAmount,
  wpsTotal,
  sstBase,
  sst,
  yearlyTotal,
  caseActual,
  formatAmountInput,
  parseAmountInput,
  formatAmount2,
};
